// src/context/BoosterNFTContext.js

import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useContext,
} from 'react';
import { ethers } from 'ethers';
import { WalletContext } from './WalletContext';
import CashBoosterNFTABI from '../abi/CashBoosterNFT.json';
import { BOOSTERNFT_ADDRESS, NETWORK_CONFIG } from '../contracts/constants';
import { toast } from 'react-toastify';

export const BoosterNFTContext = createContext();

export const BoosterNFTProvider = ({ children }) => {
  // Use the provider (which should be a Web3Provider from ethers v5) from WalletContext
  const { account, provider, chainId } = useContext(WalletContext);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [boosterNFTs, setBoosterNFTs] = useState([]);
  const [isMinting, setIsMinting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalBoostPercentage, setTotalBoostPercentage] = useState(0);

  const boosterNftContractRef = useRef(null);
  const contractsInitializedRef = useRef(false);
  const initializeRetryCountRef = useRef(0);
  const lastFetchTimeRef = useRef(0);
  const fetchTimeoutRef = useRef(null);
  const lastInitAttemptRef = useRef(0);
  const isFetchingNFTsRef = useRef(false);
  const MAX_INITIALIZE_ATTEMPTS = 3;

  // Retry helper for contract calls
  const retryContractCall = useCallback(async (fn, maxAttempts = 3) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await fn();
      } catch (error) {
        const errMsg = (error.message || '').toLowerCase();
        const dataMsg = (error?.data?.message || '').toLowerCase();
        const errorStr = JSON.stringify(error).toLowerCase();
        
        const isRetryableError = 
          error.code === -32603 ||
          error.code === -32005 ||
          error.code === -32002 ||
          error?.data?.code === -32094 ||
          error?.data?.code === -32005 ||
          errMsg.includes("limit exceeded") ||
          errMsg.includes("blockchain height exceeded") ||
          errMsg.includes("bad result from backend") ||
          errMsg.includes("rate limit") ||
          errMsg.includes("rate exceeded") ||
          errMsg.includes("too many requests") ||
          errMsg.includes("call exception") ||
          errMsg.includes("missing revert data") ||
          errMsg.includes("block is out of range") ||
          dataMsg.includes("blockchain height exceeded") ||
          errorStr.includes("rate limit") ||
          errorStr.includes("bad result");
          
        if (isRetryableError && i < maxAttempts - 1) {
          const delay = Math.pow(2, i) * 2000; // Longer delay for rate limits
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        // Only log non-RPC errors — retryable errors are transient and spammy
        if (!isRetryableError) {
          console.error("Contract call failed after retries:", error);
        }
        throw error;
      }
    }
    return null;
  }, []);

  // Fetch user's Booster NFTs
  const fetchUserBoosterNFTs = useCallback(
    async (isInitial = false) => {
      if (!account || !boosterNftContractRef.current) return;
      // Use ref to prevent concurrent calls without causing circular deps
      if (!isInitial && isFetchingNFTsRef.current) {
        return;
      }
      const now = Date.now();
      if (now - lastFetchTimeRef.current < 10000 && !isInitial) {
        return;
      }
      lastFetchTimeRef.current = now;

      isFetchingNFTsRef.current = true;
      setIsLoading(true);
      try {
        const contract = boosterNftContractRef.current;
        const balanceRaw = await retryContractCall(() => contract.balanceOf(account));
        const balance = balanceRaw.toNumber();

        if (balance === 0) {
          setBoosterNFTs([]);
          isFetchingNFTsRef.current = false;
          setIsLoading(false);
          return;
        }

        const nftPromises = [];
        for (let i = 0; i < balance; i++) {
          nftPromises.push(
            (async (index) => {
              try {
                const tokenId = await retryContractCall(() =>
                  contract.tokenOfOwnerByIndex(account, index)
                );
                const tokenURI = await retryContractCall(() => contract.tokenURI(tokenId));
                let boostValue = 0;
                try {
                  if (typeof contract.getBoostValue === 'function') {
                    const rawBoost = await retryContractCall(() => contract.getBoostValue(tokenId));
                    boostValue = rawBoost.toString();
                  }
                } catch (boostErr) {
                  // Ignoring getBoostValue errors in production
                }
                let metadata;
                try {
                  let metadataUrl = tokenURI;
                  if (tokenURI.startsWith("ipfs://")) {
                    metadataUrl = tokenURI.replace("ipfs://", "https://ipfs.io/ipfs/");
                  }
                  const response = await fetch(metadataUrl);
                  metadata = await response.json();
                } catch (metaError) {
                  console.error(`Error fetching metadata for token ${tokenId}:`, metaError);
                  metadata = {
                    name: `Booster NFT #${tokenId}`,
                    description: "Metadata unavailable",
                    image: "https://via.placeholder.com/500?text=Metadata+Unavailable"
                  };
                }
                return {
                  id: tokenId.toString(),
                  tokenId: tokenId.toString(),
                  uri: tokenURI,
                  metadata,
                  boostValue
                };
              } catch (nftErr) {
                const nftErrMsg = (nftErr.message || '').toLowerCase();
                const isRpcErr = 
                  nftErr.code === -32005 || nftErr.code === -32603 || nftErr.code === 'SERVER_ERROR' ||
                  nftErrMsg.includes('bad result') ||
                  nftErrMsg.includes('rate limit') ||
                  nftErrMsg.includes('limit exceeded');
                if (!isRpcErr) {
                  console.error(`Error processing Booster NFT at index=${index}:`, nftErr);
                }
                return null;
              }
            })(i)
          );
        }
        const nfts = await Promise.all(nftPromises);
        const validNFTs = nfts.filter((x) => x !== null);
        setBoosterNFTs(validNFTs);
      } catch (error) {
        console.error("Error fetching Booster NFTs:", error);
      } finally {
        isFetchingNFTsRef.current = false;
        setIsLoading(false);
      }
    },
    [account, retryContractCall]
  );

  // Update total boost percentage by calling the contract's getBoost method
  const updateTotalBoost = useCallback(async () => {
    if (!account || !boosterNftContractRef.current) {
      setTotalBoostPercentage(0);
      return;
    }
    try {
      const boostFromContract = await boosterNftContractRef.current.getBoost(account);
      const boostInt = boostFromContract.toNumber();
      setTotalBoostPercentage(boostInt);
    } catch (error) {
      console.error("Error fetching boost from contract:", error);
      setTotalBoostPercentage(0);
    }
  }, [account]);

  // Update total boost whenever boosterNFTs or account changes
  useEffect(() => {
    updateTotalBoost();
  }, [boosterNFTs, account, updateTotalBoost]);

  /**
   * Mint 1 or multiple Booster NFTs.
   * Sends exactly 0.01 ETH per NFT.
   */
  const mintNFTs = useCallback(
    async (amount = 1) => {
      if (!account) {
        console.error("No account connected, cannot mint.");
        return false;
      }
      if (!isInitialized) {
        console.error("Contract not initialized, cannot mint.");
        return false;
      }
      if (amount <= 0) {
        console.error(`Invalid mint amount: ${amount}`);
        return false;
      }
      setIsMinting(true);
      try {
        // Calculate exact price in wei (0.01 ETH per NFT)
        const mintPriceWei = ethers.utils.parseUnits("0.01", 18);
        const totalPriceWei = mintPriceWei.mul(ethers.BigNumber.from(amount));

        const iface = new ethers.utils.Interface(CashBoosterNFTABI);

        let data;
        if (amount === 1) {
          data = iface.encodeFunctionData("mintBoosterNFT", []);
        } else {
          data = iface.encodeFunctionData("mintMultipleBoosterNFTs", [amount]);
        }

        const transactionParameters = {
          to: BOOSTERNFT_ADDRESS,
          from: account,
          value: totalPriceWei.toHexString(),
          data: data
        };

        const txHash = await window.ethereum.request({
          method: 'eth_sendTransaction',
          params: [transactionParameters],
        });

        let receipt = null;
        let attempts = 0;
        const maxAttempts = 30;

        while (!receipt && attempts < maxAttempts) {
          try {
            receipt = await provider.getTransactionReceipt(txHash);
            if (!receipt) {
              await new Promise(resolve => setTimeout(resolve, 2000));
              attempts++;
            }
          } catch (error) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            attempts++;
          }
        }

        if (!receipt) {
          toast.info("Transaction sent but confirmation is taking longer than expected. Check your wallet for status.");
          return true;
        }

        if (receipt.status === 0) {
          console.error("Transaction failed on-chain");
          toast.error("Transaction failed on the blockchain. The contract may be paused or have reached its maximum supply.");
          return false;
        }

        // Update NFT list
        await fetchUserBoosterNFTs();

        toast.success(`Successfully minted ${amount} Booster NFT${amount > 1 ? 's' : ''}!`);
        return true;
      } catch (error) {
        console.error("Error in mintNFTs:", error);

        if (error.code === 4001 || (error.message && error.message.includes("user rejected"))) {
          toast.info("Transaction was rejected by the user.");
        } else if (error.message && error.message.includes("insufficient funds")) {
          toast.error("Insufficient funds to complete the transaction.");
        } else if (error.message && error.message.includes("paused")) {
          toast.error("The contract is currently paused. Please try again later.");
        } else if (error.message && error.message.includes("max supply")) {
          toast.error("Maximum NFT supply has been reached.");
        } else if (error.message && error.message.includes("400")) {
          toast.error("Network error. Please try again in a few moments or refresh the page.");
        } else {
          toast.error(`Failed to mint NFT. ${error.message || "Please try again."}`);
        }

        return false;
      } finally {
        setIsMinting(false);
      }
    },
    [account, isInitialized, fetchUserBoosterNFTs, provider]
  );

  /**
   * Initialize the Booster NFT contract.
   */
  const initializeContracts = useCallback(async () => {
    if (!provider || !account || isInitializing || contractsInitializedRef.current) return;
    
    // Throttle: prevent repeated init attempts within 15 seconds
    const now = Date.now();
    if (now - lastInitAttemptRef.current < 15000) {
      return;
    }
    lastInitAttemptRef.current = now;
    
    setIsInitializing(true);
    try {
      const network = await provider.getNetwork();
      if (network.chainId !== NETWORK_CONFIG.chainId) {
        setIsInitializing(false);
        return;
      }
      const code = await provider.getCode(BOOSTERNFT_ADDRESS);
      if (code === "0x") {
        setIsInitializing(false);
        return;
      }
      const signer = provider.getSigner();
      const boosterContract = new ethers.Contract(BOOSTERNFT_ADDRESS, CashBoosterNFTABI, signer);
      const codeCheck = await provider.getCode(boosterContract.address);
      if (codeCheck === "0x") {
        setIsInitializing(false);
        return;
      }
      boosterNftContractRef.current = boosterContract;
      contractsInitializedRef.current = true;
      setIsInitialized(true);
      await fetchUserBoosterNFTs(true);
    } catch (error) {
      const errMsg = (error.message || '').toLowerCase();
      const errorStr = JSON.stringify(error).toLowerCase();
      
      // Check if it's a rate limit or RPC error
      const isRateLimitError = 
        error.code === -32005 ||
        error.code === -32603 ||
        error.code === -32002 ||
        error?.data?.code === -32005 ||
        errMsg.includes('rate limit') ||
        errMsg.includes('limit exceeded') ||
        errMsg.includes('rate exceeded') ||
        errMsg.includes('too many requests') ||
        errorStr.includes('rate limit') ||
        errorStr.includes('limit exceeded');
      const isRpcError = 
        errMsg.includes('bad result') ||
        errMsg.includes('blockchain height exceeded') ||
        errMsg.includes('call exception');
        
      // Only log and show toast for non-rate-limit errors
      if (!isRateLimitError && !isRpcError) {
        console.error("Failed to initialize Booster NFT contract:", error);
      }
      
      // Do NOT auto-retry on rate limit errors to avoid spamming RPC
      if (isRateLimitError) {
        // Extend throttle by 30 seconds
        lastInitAttemptRef.current = Date.now() + 30000;
        boosterNftContractRef.current = null;
        contractsInitializedRef.current = false;
        setIsInitialized(false);
      } else if (initializeRetryCountRef.current < MAX_INITIALIZE_ATTEMPTS) {
        initializeRetryCountRef.current += 1;
        const delay = Math.pow(2, initializeRetryCountRef.current) * 2000;
        setTimeout(initializeContracts, delay);
      } else {
        toast.error(`Failed to initialize Booster NFT contract: ${error.message}`, { autoClose: 5000 });
        boosterNftContractRef.current = null;
        contractsInitializedRef.current = false;
        setIsInitialized(false);
        initializeRetryCountRef.current = 0;
      }
    } finally {
      setIsInitializing(false);
    }
  }, [provider, account, isInitializing, fetchUserBoosterNFTs]);

  /**
   * Poll for NFT updates every 60 seconds.
   */
  useEffect(() => {
    if (isInitialized && account) {
      if (fetchTimeoutRef.current) clearInterval(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setInterval(() => {
        const now = Date.now();
        if (now - lastFetchTimeRef.current >= 60000) {
          fetchUserBoosterNFTs();
        }
      }, 60000);
    }
    return () => {
      if (fetchTimeoutRef.current) {
        clearInterval(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [isInitialized, account, fetchUserBoosterNFTs]);

  /**
   * Reinitialize if provider/account/chain changes.
   */
  useEffect(() => {
    if (
      provider &&
      account &&
      chainId === NETWORK_CONFIG.chainId &&
      !isInitialized &&
      !isInitializing &&
      !contractsInitializedRef.current
    ) {
      initializeRetryCountRef.current = 0;
      initializeContracts();
    } else if ((!account || chainId !== NETWORK_CONFIG.chainId) && isInitialized) {
      setIsInitialized(false);
      contractsInitializedRef.current = false;
      boosterNftContractRef.current = null;
      setBoosterNFTs([]);
      isFetchingNFTsRef.current = false;
      setIsInitializing(false);
      initializeRetryCountRef.current = 0;
    }
  }, [provider, account, chainId, isInitialized, isInitializing, initializeContracts]);

  /**
   * Clean up intervals on unmount.
   */
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearInterval(fetchTimeoutRef.current);
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      boosterNFTs,
      isInitialized,
      isMinting,
      isLoading,
      fetchUserBoosterNFTs,
      mintNFTs,
      totalBoostPercentage
    }),
    [
      boosterNFTs,
      isInitialized,
      isMinting,
      isLoading,
      fetchUserBoosterNFTs,
      mintNFTs,
      totalBoostPercentage
    ]
  );

  return (
    <BoosterNFTContext.Provider value={contextValue}>
      {children}
    </BoosterNFTContext.Provider>
  );
};

export default BoosterNFTProvider;